import { FormEvent, useEffect, useState } from "react";
import { Download, FileUp, Plus } from "lucide-react";

import { leaveApi } from "../api";
import { useConfirm } from "#/components/common/ConfirmProvider";
import { LeaveBackButton } from "../components/LeaveBackButton";
import { LeaveError } from "../components/LeaveError";
import { LeaveLoading } from "../components/LeaveLoading";
import { LeavePageHeader } from "../components/LeavePageHeader";
import { LeavePagination } from "../components/LeavePagination";
import { LeaveSetupTabs } from "../components/LeaveSetupTabs";
import { useAsyncData, useMutation, usePagination } from "../hooks";
import type {
  Holiday,
  HolidayCreateRequest,
  HolidayImportResponse,
} from "../types";

const TEMPLATE_URL = "/templates/holiday_upload_template.xlsx";

export default function LeaveHolidayConfigPage() {
  const pagination = usePagination(0, 10);
  const confirm = useConfirm();
  const [editing, setEditing] = useState<Holiday | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [importResult, setImportResult] =
    useState<HolidayImportResponse | null>(null);

  const { data, loading, error, reload } = useAsyncData(
    () => leaveApi.holidays(pagination.page, pagination.size),
    [pagination.page, pagination.size],
  );

  const save = useMutation(async (payload: HolidayCreateRequest) => {
    if (editing) {
      return leaveApi.updateHoliday(editing.id, payload);
    }

    return leaveApi.createHoliday(payload);
  });

  const upload = useMutation((file: File) => leaveApi.importHolidays(file));

  async function handleSubmit(payload: HolidayCreateRequest) {
    await save.mutate(payload);
    setEditing(null);
    setShowForm(false);
    await reload();
  }

  async function handleToggle(item: Holiday) {
    const action = item.active ? "deactivate" : "activate";

    if (!(await confirm(`Are you sure you want to ${action} ${item.name}?`))) {
      return;
    }

    if (item.active) {
      await leaveApi.deactivateHoliday(item.id);
    } else {
      await leaveApi.activateHoliday(item.id);
    }

    await reload();
  }

  async function handleUpload(file: File) {
    const result = await upload.mutate(file);
    setImportResult(result);
    await reload();
  }

  return (
    <div className="space-y-5">
      <LeavePageHeader
        title="Holiday Calendar"
        actions={
          <div className="flex flex-wrap gap-2">
            <LeaveBackButton to="/hr/leave/setup" label="Setup" />
            <a
              href={TEMPLATE_URL}
              download
              className="inline-flex items-center gap-2 rounded-xl border px-4 py-2 text-sm font-bold hover:bg-slate-50"
            >
              <Download className="h-4 w-4" />
              Download Template
            </a>

            <label className="inline-flex cursor-pointer items-center gap-2 rounded-xl border px-4 py-2 text-sm font-bold hover:bg-slate-50">
              <FileUp className="h-4 w-4" />
              Import Excel
              <input
                type="file"
                accept=".xlsx,.xls"
                className="hidden"
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  if (file) handleUpload(file);
                  event.target.value = "";
                }}
              />
            </label>

            <button
              type="button"
              onClick={() => {
                setEditing(null);
                setShowForm(true);
              }}
              className="inline-flex items-center gap-2 rounded-xl bg-emerald-700 px-4 py-2 text-sm font-bold text-white hover:bg-emerald-800"
            >
              <Plus className="h-4 w-4" />
              Add Holiday
            </button>
          </div>
        }
      />

      <LeaveSetupTabs />

      {error ? <LeaveError message={error} onRetry={reload} /> : null}
      {save.error ? <LeaveError message={save.error} /> : null}
      {upload.error ? <LeaveError message={upload.error} /> : null}
      {upload.loading ? <LeaveLoading label="Importing holidays..." /> : null}

      {importResult ? <ImportResult result={importResult} /> : null}

      {showForm || editing ? (
        <HolidayForm
          initialValue={editing}
          submitting={save.loading}
          onSubmit={handleSubmit}
          onCancel={() => {
            setEditing(null);
            setShowForm(false);
          }}
        />
      ) : null}

      {loading ? <LeaveLoading label="Loading holidays..." /> : null}

      {data ? (
        <>
          <HolidayTable
            items={data.content}
            onEdit={(item) => {
              setEditing(item);
              setShowForm(true);
            }}
            onToggleActive={handleToggle}
          />

          <LeavePagination
            page={data}
            onPageChange={pagination.setPage}
            onSizeChange={pagination.setSize}
          />
        </>
      ) : null}
    </div>
  );
}

function ImportResult({ result }: { result: HolidayImportResponse }) {
  return (
    <div className="rounded-3xl border bg-white p-5 shadow-sm">
      <div className="grid gap-3 md:grid-cols-3">
        <Stat label="Imported" value={result.imported} tone="good" />
        <Stat label="Skipped" value={result.skipped} tone="warn" />
        <Stat label="Errors" value={result.errors.length} />
      </div>

      {result.errors.length ? (
        <div className="mt-4 overflow-hidden rounded-2xl border">
          <table className="w-full text-left text-sm">
            <thead className="bg-red-50 text-xs uppercase tracking-wide text-red-700">
              <tr>
                <th className="px-4 py-3">Row</th>
                <th className="px-4 py-3">Reason</th>
              </tr>
            </thead>

            <tbody className="divide-y">
              {result.errors.slice(0, 50).map((error) => (
                <tr key={`${error.row}-${error.message}`}>
                  <td className="px-4 py-3 font-bold">{error.row}</td>
                  <td className="px-4 py-3">{error.message}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone?: "good" | "warn";
}) {
  const color =
    tone === "good"
      ? "text-emerald-700"
      : tone === "warn"
        ? "text-amber-700"
        : "text-slate-900";

  return (
    <div className="rounded-2xl bg-slate-50 p-4">
      <div className="text-xs font-bold uppercase tracking-wide text-slate-500">
        {label}
      </div>
      <div className={`mt-1 text-2xl font-black ${color}`}>{value}</div>
    </div>
  );
}

function HolidayForm({
  initialValue,
  submitting,
  onSubmit,
  onCancel,
}: {
  initialValue?: Holiday | null;
  submitting?: boolean;
  onSubmit: (payload: HolidayCreateRequest) => Promise<void> | void;
  onCancel: () => void;
}) {
  const [form, setForm] = useState<HolidayCreateRequest>({
    holidayDate: "",
    name: "",
    description: "",
    recurringAnnually: false,
    active: true,
  });

  useEffect(() => {
    if (initialValue) {
      setForm({
        holidayDate: initialValue.holidayDate,
        name: initialValue.name,
        description: initialValue.description || "",
        recurringAnnually: initialValue.recurringAnnually,
        active: initialValue.active,
      });
    }
  }, [initialValue]);

  async function submit(event: FormEvent) {
    event.preventDefault();

    await onSubmit({
      holidayDate: form.holidayDate,
      name: form.name.trim(),
      description: form.description?.trim() || null,
      recurringAnnually: Boolean(form.recurringAnnually),
      active: Boolean(form.active),
    });
  }

  return (
    <form
      onSubmit={submit}
      className="grid gap-4 rounded-3xl border bg-white p-5 shadow-sm md:grid-cols-2"
    >
      <Field label="Holiday Date" required>
        <input
          type="date"
          className="input"
          value={form.holidayDate}
          onChange={(event) =>
            setForm((previous) => ({
              ...previous,
              holidayDate: event.target.value,
            }))
          }
          required
        />
      </Field>

      <Field label="Holiday Name" required>
        <input
          className="input"
          value={form.name}
          onChange={(event) =>
            setForm((previous) => ({
              ...previous,
              name: event.target.value,
            }))
          }
          placeholder="Madaraka Day"
          required
        />
      </Field>

      <div className="md:col-span-2">
        <Field label="Description">
          <textarea
            className="input min-h-24"
            value={form.description || ""}
            onChange={(event) =>
              setForm((previous) => ({
                ...previous,
                description: event.target.value,
              }))
            }
            placeholder="Optional notes"
          />
        </Field>
      </div>

      <Check
        label="Recurring Annually"
        checked={Boolean(form.recurringAnnually)}
        onChange={(value) =>
          setForm((previous) => ({
            ...previous,
            recurringAnnually: value,
          }))
        }
      />

      <Check
        label="Active"
        checked={Boolean(form.active)}
        onChange={(value) =>
          setForm((previous) => ({
            ...previous,
            active: value,
          }))
        }
      />

      <div className="flex justify-end gap-2 md:col-span-2">
        <button
          type="button"
          onClick={onCancel}
          className="rounded-xl border px-4 py-2 text-sm font-bold hover:bg-slate-50"
        >
          Cancel
        </button>

        <button
          type="submit"
          disabled={submitting}
          className="rounded-xl bg-emerald-700 px-4 py-2 text-sm font-bold text-white hover:bg-emerald-800 disabled:opacity-60"
        >
          {submitting
            ? "Saving..."
            : initialValue
              ? "Update Holiday"
              : "Create Holiday"}
        </button>
      </div>
    </form>
  );
}

function HolidayTable({
  items,
  onEdit,
  onToggleActive,
}: {
  items: Holiday[];
  onEdit: (item: Holiday) => void;
  onToggleActive: (item: Holiday) => void;
}) {
  return (
    <div className="overflow-hidden rounded-2xl border bg-white shadow-sm">
      <table className="w-full text-left text-sm">
        <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
          <tr>
            <th className="px-4 py-3">Date</th>
            <th className="px-4 py-3">Holiday</th>
            <th className="px-4 py-3">Recurring</th>
            <th className="px-4 py-3">Status</th>
            <th className="px-4 py-3 text-right">Action</th>
          </tr>
        </thead>

        <tbody className="divide-y">
          {items.map((item) => (
            <tr key={item.id} className="hover:bg-slate-50">
              <td className="px-4 py-3 font-bold">{item.holidayDate}</td>

              <td className="px-4 py-3">
                <div className="font-bold text-slate-900">{item.name}</div>
                <div className="text-xs text-slate-500">
                  {item.description || "-"}
                </div>
              </td>

              <td className="px-4 py-3">
                {item.recurringAnnually ? "Yes" : "No"}
              </td>

              <td className="px-4 py-3">
                <Status active={item.active} />
              </td>

              <td className="px-4 py-3">
                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => onEdit(item)}
                    className="rounded-xl border px-3 py-1.5 text-xs font-bold hover:bg-slate-50"
                  >
                    Edit
                  </button>

                  <button
                    type="button"
                    onClick={() => onToggleActive(item)}
                    className="rounded-xl border px-3 py-1.5 text-xs font-bold hover:bg-slate-50"
                  >
                    {item.active ? "Deactivate" : "Activate"}
                  </button>
                </div>
              </td>
            </tr>
          ))}

          {!items.length ? (
            <tr>
              <td colSpan={5} className="px-4 py-10 text-center text-slate-500">
                No holidays configured.
              </td>
            </tr>
          ) : null}
        </tbody>
      </table>
    </div>
  );
}

function Status({ active }: { active: boolean }) {
  return (
    <span
      className={`rounded-full px-3 py-1 text-xs font-bold ${
        active
          ? "bg-emerald-100 text-emerald-700"
          : "bg-slate-200 text-slate-700"
      }`}
    >
      {active ? "Active" : "Inactive"}
    </span>
  );
}

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <label className="space-y-2">
      <div className="text-sm font-semibold text-slate-700">
        {label}
        {required ? <span className="ml-1 text-red-500">*</span> : null}
      </div>
      {children}
    </label>
  );
}

function Check({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (value: boolean) => void;
}) {
  return (
    <label className="flex items-center gap-3 rounded-2xl border p-4 text-sm font-semibold text-slate-700">
      <input
        type="checkbox"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
        className="h-4 w-4"
      />
      {label}
    </label>
  );
}
